import type {
  EnhancedProductFamily,
  EnhancedProductFamilyId,
  EnhancedProductGroup,
  EnhancedProductGroupId,
  EnhancedProductVariant,
} from '#api/enhancements/enhancedTmsApiTypes'
import type { AppProductFamily, AppProductGroup, AppProductVariant, ProductPath } from '#app/types/AppTypes'
import { defaultPrintOptions } from '#features/PrintOptions/PrintOptions'
import { hasPrintOptionsChanged } from '#features/PrintOptions/utils'
import { isProductFamily, isProductGroup, isProductVariant } from '#utils/EntryTypeGuards'
import { matchCodeGroups } from '#utils/matchCodeGroups'

export type ElementState = 'filtered' | 'unfiltered' | undefined
// in theory this does not need to be partial - the filter should have visited every element on its own
export type FilterResult = Partial<Record<ProductPath, ElementState>>
export type FilterWriter = (element: AppProductGroup | AppProductFamily | AppProductVariant, shouldBeKept: boolean) => void
export type FilterComparator = 'AND' | 'OR'
/**
 * A filter should:
 * - accept any element
 * - decide on its own if it should run (for example with the previousResult)
 * - take the element given into consideration
 *   - specifically, it will be called for all subgroups, families and variants with possibly calculations before
 *   - write a result for all checked elements into the passed bucket
 */
export type Filter<T = void> = (element: AppProductGroup | AppProductFamily | AppProductVariant, bucket: FilterResult, writer: FilterWriter) => T

export const getAllGroups = (group: AppProductGroup): AppProductGroup[] => [group, ...group.subGroups.flatMap(getAllGroups)]
export const getAllSubgroups = (group: AppProductGroup): AppProductGroup[] => [...group.subGroups.flatMap(getAllGroups)]
const selectId = (item: AppProductGroup | AppProductFamily | AppProductVariant) => item.path
const makeBucketWriter = (combinationType: FilterComparator, bucket: FilterResult): FilterWriter => {
  return (element, shouldBeKept) => {
    const id = selectId(element)
    const priorResult = bucket[id]
    const writeValue = shouldBeKept ? 'unfiltered' : 'filtered'
    if (!priorResult) {
      bucket[id] = writeValue
      return
    }

    // it's unfiltered, boolean operator AND -> the oncoming value either filters it or keeps it unfiltered
    if (priorResult === 'unfiltered' && combinationType === 'AND') {
      bucket[id] = writeValue
      return
    }

    // it's filtered, boolean operator OR -> the oncoming value may remove the filter or just keep it
    if (priorResult === 'filtered' && combinationType === 'OR') {
      bucket[id] = writeValue
    }
  }
}

// reusable function of selecting the property, establishing ground truth

/** Has a product variant insights? */
export const productVariantHasInsights = (
  variant: AppProductVariant,
  { includeOnlySelectedVariants }: { includeOnlySelectedVariants?: boolean } = {},
) => {
  if (includeOnlySelectedVariants && variant.parentFamily.selectedVariant !== variant) {
    return false
  }

  return variant.insightsData ? Object.values(variant.insightsData).some(Boolean) : false
}
/** Does the family contain variants with insights? */
export const productFamilyContainsInsights = (family: AppProductFamily, options: { includeOnlySelectedVariants?: boolean } = {}) =>
  family.variants.some((v) => productVariantHasInsights(v, options))
/** Does the group, it's subgroups or any families in groups or subgroups have any insights?  */
export const productGroupContainsInsights = (group: AppProductGroup, options: { includeOnlySelectedVariants?: boolean } = {}) =>
  getAllGroups(group).some((g) => g.families.some((f) => productFamilyContainsInsights(f, options)))

export const productVariantIsFavorited = (
  variant: AppProductVariant,
  { includeOnlySelectedVariants }: { includeOnlySelectedVariants?: boolean } = {},
) => {
  if (includeOnlySelectedVariants && variant.parentFamily.selectedVariant !== variant) {
    return false
  }

  return !!variant.isFavorite
}
/** Is the family favorited? */
export const productFamilyIsFavorited = (family: AppProductFamily) => family.isFavorite
/** Is the group favorited */
export const productGroupIsFavorited = (group: AppProductGroup) => group.isFavorite

export const productFamilyContainsFavorites = (
  family: AppProductFamily,
  { includeOnlySelectedVariants }: { includeOnlySelectedVariants?: boolean } = {},
) =>
  productFamilyIsFavorited(family) ||
  (!includeOnlySelectedVariants && family.hasNonSelectedFavorites) ||
  family.variants.some((x) => productVariantIsFavorited(x))

/** Is the group, it's subgroups or any families in groups or subgroups favorited? */
export const productGroupContainsFavorites = (group: AppProductGroup, options: { includeOnlySelectedVariants?: boolean } = {}) =>
  getAllGroups(group).some((g) => productGroupIsFavorited(g) || g.families.some((f) => productFamilyContainsFavorites(f, options)))

/** Is the family variant selection edited? */
export const productFamilyIsEdited = (family: AppProductFamily) => family.isUserConfigured
/** Is the product group, it's subgroups or any families in the group or subgroups edited?  */
export const productGroupContainsEdits = (group: AppProductGroup) => getAllGroups(group).some((x) => x.families.some(productFamilyIsEdited))

/** Has the variant any print options selected */
export const productVariantHasPrintChanges = (variant: AppProductVariant) => hasPrintOptionsChanged(variant.printOptions)
/** Has the family and variants where the print options changed? */
export const productFamilyIncludesPrintChanges = (family: AppProductFamily) => family.variants.some(productVariantHasPrintChanges)
/** Has the product group, it's subgroups or any families in the group or subgroups any print options changed? */
export const productGroupIncludesPrintChanges = (group: AppProductGroup) =>
  getAllGroups(group).some((x) => x.families.some(productFamilyIncludesPrintChanges))

/** Is this variant supposed to be printed? */
export const productVariantIsPrinted = (variant: AppProductVariant) => {
  if (variant.printOptions) {
    return !variant.printOptions.doNotPrint
  }
  return !defaultPrintOptions.doNotPrint
}
/** Has the family and variants a selected variant that is being printed */
export const productFamilyIsPrinted = (family: AppProductFamily) => {
  if (!family.selectedVariant) {
    return !defaultPrintOptions.doNotPrint
  }
  return productVariantIsPrinted(family.selectedVariant)
}
/** Has the product group, it's subgroups or any families in the group or subgroups a selected variant that is being printed? */
export const productGroupIsPrinted = (group: AppProductGroup) => getAllGroups(group).some((x) => x.families.some(productFamilyIsPrinted))

export const hasProductGroupAnyData = (group: AppProductGroup) => {
  // groups with families may have data
  if (group.families.length > 0) {
    // families with one variant cannot be selected anyway, but should still be displayed as read only
    return group.families.some((fam) => fam.variants.length > 0)
  }
  // any subgroup may have data
  if (group.subGroups.length > 0) {
    return group.subGroups.some(hasProductGroupAnyData)
  }
  // no subgroups, no families -> no data
  return false
}

/** Does the ProductGroup contain the search query */
export const productGroupContainsString = (productGroup: EnhancedProductGroup, searchText: string) =>
  productGroup.title.toLowerCase().includes(searchText.toLowerCase())
/** Does the ProductFamily contain the search query */
export const productFamilyContainsString = (productFamily: EnhancedProductFamily, searchText: string) => {
  if (productFamily.title.toLowerCase().includes(searchText.toLowerCase()) || productFamily.id.includes(searchText)) {
    return true
  }
  const asCode = matchCodeGroups(searchText)
  // assumption: does not need lowercasing, they are digits, usually, hopefully.
  return asCode.possibleCode && productFamily.id.includes(asCode.groups.code)
}
/** Does the ProductVariant contain the search query */
export const productVariantContainsString = (productFamily: EnhancedProductFamily, productVariant: EnhancedProductVariant, searchText: string) => {
  if (productVariant.title.toLowerCase().includes(searchText.toLowerCase())) {
    return true
  }
  const asCode = matchCodeGroups(searchText)
  return (
    asCode.possibleCode &&
    productFamily.id.includes(asCode.groups.code) &&
    productVariant.id.toLowerCase().includes(asCode.groups.variant.toLowerCase())
  )
}
/** Does the ProductGroup | ProductFamily | ProductVariant contain the search query */
export const productElementContainsString = (productElement: AppProductGroup | AppProductFamily | AppProductVariant, searchText: string) => {
  if (isProductGroup(productElement)) {
    return productGroupContainsString(productElement, searchText)
  }
  if (isProductFamily(productElement)) {
    return productFamilyContainsString(productElement, searchText)
  }
  return productVariantContainsString(productElement.parentFamily, productElement, searchText)
}

/** Filter operator if the title includes a string */
export const makeElementContainsStringOperator = (
  searchText: string,
  { includeOnlySelectedVariants }: { includeOnlySelectedVariants?: boolean } = {},
): Filter => {
  return (element, bucket, writer) => {
    const found = productElementContainsString(element, searchText)
    writer(element, found)

    if (found && isProductVariant(element)) {
      if (includeOnlySelectedVariants && element.parentFamily.selectedVariant !== element) {
        bucket[element.path] = 'filtered'
        return
      }

      writer(element.parentFamily, true)
      let group: AppProductGroup | undefined = element.parentFamily.parentGroup
      while (group) {
        writer(group, true)
        group = group.parentGroup
      }
    }
    if (found && isProductFamily(element)) {
      if (includeOnlySelectedVariants) {
        if (element.selectedVariant) {
          writer(element.selectedVariant, true)
        }
      } else {
        for (const variant of element.variants) {
          writer(variant, true)
        }
      }
    }
    if (found && isProductGroup(element)) {
      const allGroups = getAllGroups(element)
      for (const group of allGroups) {
        writer(group, true)
        for (const family of group.families) {
          writer(family, true)
          if (includeOnlySelectedVariants) {
            if (family.selectedVariant) {
              writer(family.selectedVariant, true)
            }
          } else {
            for (const variant of family.variants) {
              writer(variant, true)
            }
          }
        }
      }
    }
  }
}

/** Filter operator to test if a variant has insights or family or groups contains a variant with insights */
export const makeElementHasInsightsOperator =
  (options: { includeOnlySelectedVariants?: boolean } = {}): Filter =>
  (element, _bucket, writer) => {
    // as a group, I'll be checking my families
    if (isProductGroup(element)) {
      const result = productGroupContainsInsights(element, options)
      writer(element, result)
      return
    }
    // as a family, I'll be checking variants
    if (isProductFamily(element)) {
      const result = productFamilyContainsInsights(element, options)
      writer(element, result)
      return
    }
    const result = productVariantHasInsights(element, options)
    writer(element, result)
  }

/** Filter operator to test if a group or family has been favorited, or groups containing families with favorites  */
export const makeElementHasFavoritesOperator =
  (options: { includeOnlySelectedVariants?: boolean } = {}): Filter =>
  (element, _bucket, writer) => {
    if (isProductGroup(element)) {
      const result = productGroupContainsFavorites(element, options)
      writer(element, result)
      return
    }

    if (isProductFamily(element)) {
      // sensible to include all family entires if the group has been favorited
      const result = productFamilyContainsFavorites(element, options)
      writer(element, result)
      return
    }

    const result =
      element.parentFamily.isFavorite ||
      (!options.includeOnlySelectedVariants && element.parentFamily.hasNonSelectedFavorites) ||
      productVariantIsFavorited(element, options)
    writer(element, result)
  }

/** Filter operator to test if a family has been edited or groups contain families with edits */
export const elementHasBeenEditedOperator: Filter = (element, _bucket, writer) => {
  if (isProductGroup(element)) {
    const result = productGroupContainsEdits(element)
    writer(element, result)
    return
  }

  if (isProductFamily(element)) {
    const result = productFamilyIsEdited(element)
    writer(element, result)
    return
  }

  const result = productFamilyIsEdited(element.parentFamily)
  writer(element, result)
}

/** Filter operator to test if a variant has changed print options or if a group or family contains a variant with changed print entries */
export const elementHasPrintOptions: Filter = (element, _bucket, writer) => {
  if (isProductGroup(element)) {
    const result = productGroupIncludesPrintChanges(element)
    writer(element, result)
    return
  }

  if (isProductFamily(element)) {
    const result = productFamilyIncludesPrintChanges(element)
    writer(element, result)
    return
  }

  const result = productVariantHasPrintChanges(element)
  writer(element, result)
  return
}

/** Filter operator to test if a variant has changed print options or if a group or family contains a variant with changed print entries */
export const elementIsPrinted: Filter = (element, bucket, _writer) => {
  if (isProductGroup(element)) {
    const result = productGroupIsPrinted(element)
    if (result) {
      if (!bucket[element.path]) {
        bucket[element.path] = 'unfiltered'
      }
    } else {
      bucket[element.path] = 'filtered'
    }
    return
  }

  if (isProductFamily(element)) {
    const result = productFamilyIsPrinted(element)
    if (result) {
      if (!bucket[element.path]) {
        bucket[element.path] = 'unfiltered'
      }
    } else {
      bucket[element.path] = 'filtered'
    }
    return
  }

  const result = productVariantIsPrinted(element)
  if (result) {
    if (!bucket[element.path]) {
      bucket[element.path] = 'unfiltered'
    }
  } else {
    bucket[element.path] = 'filtered'
  }
  return
}

/** Filter operator to filter all groups that have no families in themselves or subgroups */
export const elementHasAnyFamilies: Filter = (element, _bucket, writer) => {
  if (isProductGroup(element)) {
    const result = hasProductGroupAnyData(element)
    writer(element, result)
    return
  }
  // anything else (families and variants) should be kept
  writer(element, true)
  return
}

/** Filter operator to unfilter all families and its variants if any adjacent family in that group has not been filtered */
export const elementKeepAllFamiliesOperator: Filter = (element, bucket) => {
  if (isProductGroup(element)) {
    const allGroups = getAllGroups(element)
    const hasUnfilteredGroups = allGroups.some((x) => bucket[x.path] === 'unfiltered')
    if (hasUnfilteredGroups) {
      bucket[element.path] = 'unfiltered'
    }

    const hasUnfilteredFamiliesOrVariants = element.families.some(
      (x) => bucket[x.path] === 'unfiltered' || x.variants.some((y) => bucket[y.path] === 'unfiltered'),
    )
    if (!hasUnfilteredFamiliesOrVariants) {
      return
    }
    bucket[element.path] = 'unfiltered'
    for (const family of element.families) {
      bucket[family.path] = 'unfiltered'
      for (const variant of family.variants) {
        bucket[variant.path] = 'unfiltered'
      }
    }
  }
  // otherwise there is no work necessary
}

/** Filter operator to include any family or group that has either a variant or a family that it's unfiltered (bottom-up inclusion) */
export const elementKeepUnfilteredVariantsAndFamiliesOperator: Filter = (element, bucket) => {
  if (isProductGroup(element)) {
    const allGroups = getAllGroups(element)
    const hasUnfilteredGroups = allGroups.some((x) => bucket[x.path] === 'unfiltered')
    if (hasUnfilteredGroups) {
      bucket[element.path] = 'unfiltered'
    }

    // post process all families and find if there are unfiltered elements
    const familyFilterState = element.families.map((x) => {
      let unfiltered = bucket[x.path] === 'unfiltered'
      if (!unfiltered) {
        unfiltered = x.variants.some((y) => bucket[y.path] === 'unfiltered')
      }
      if (unfiltered) {
        // include all variants
        for (const y of x.variants) {
          bucket[y.path] = 'unfiltered'
        }
        bucket[x.path] = 'unfiltered'
      }
      return bucket[x.path]
    })
    if (familyFilterState.includes('unfiltered')) {
      bucket[element.path] = 'unfiltered'
    }
  }
}

/** Unfilters all families if the group matches */
export const makeElementKeepFamiliesOfGroupOperator = (groupIds: EnhancedProductGroupId[]): Filter => {
  return (element, bucket) => {
    if (isProductGroup(element)) {
      for (const groupId of groupIds) {
        if (element.id !== groupId) {
          continue
        }
        for (const family of element.families) {
          bucket[family.path] = 'unfiltered'
          for (const variant of family.variants) {
            bucket[variant.path] = 'unfiltered'
          }
        }
      }
    }
  }
}

/** Unfilters all families if the group matches */
export const makeElementKeepFamiliesOperator = (familyIds: EnhancedProductFamilyId[]): Filter => {
  return (element, _bucket, writer) => {
    if (isProductGroup(element)) {
      const groups = getAllGroups(element)
      for (const group of groups) {
        writer(
          element,
          group.families.some((x) => familyIds.includes(x.id)),
        )
      }
      return
    }
    if (isProductFamily(element)) {
      writer(element, familyIds.includes(element.id))
      return
    }
    if (isProductVariant(element)) {
      writer(element, familyIds.includes(element.parentFamily.id))
    }
  }
}

export const makeGroupNotPartOfWellKnownOperator = (groupIds: EnhancedProductGroupId[]): Filter => {
  return (element, bucket, _writer) => {
    if (isProductGroup(element)) {
      // additional exception: NonViewOspecGroup with view numbers often show up that we willingly exclude
      const isPartOfFilteredGroups = groupIds.includes(element.id) || element.id.includes('NonViewOspecGroup')
      if (!isPartOfFilteredGroups) {
        return
      }
      bucket[element.path] = 'filtered'
      const groups = [element, ...getAllGroups(element)]
      for (const group of groups) {
        bucket[group.path] = 'filtered'
        for (const family of group.families) {
          bucket[family.path] = 'filtered'
          for (const variant of family.variants) {
            bucket[variant.path] = 'filtered'
          }
        }
      }
    }
  }
}

/** Creates an operator that allows filtering out all subgroups, families and variants that are contained within */
export const makeGroupIsCollapsedOperator = (collapsed: Set<EnhancedProductGroupId>): Filter => {
  // currently explicitly overwriting to make a reasonable collapsing algorithm (?)
  return (element, bucket, _writer) => {
    if (isProductGroup(element) && collapsed.has(element.id)) {
      const groups = getAllGroups(element)
      for (const group of groups) {
        if (group.path === element.path) {
        } else {
          bucket[group.path] = 'filtered'
        }
        for (const family of group.families) {
          bucket[family.path] = 'filtered'
          for (const variant of family.variants) {
            bucket[variant.path] = 'filtered'
          }
        }
      }
    }
  }
}

export const elementRemoveUnselectedVariantOperator: Filter = (element, _bucket, writer) => {
  if (isProductVariant(element) && element.parentFamily.selectedVariant !== element) {
    writer(element, false)
  }
}

/** Create a set of filter operators based on default operators, filter order is in order of type keys. */
export const makeDefaultFilterOperators = ({
  preFilters,
  searchText,
  hasInsights,
  hasEdits,
  hasFavorites,
  hasPrintOptions,
  displayFamilies,
  includeAllVariantsForMatchedFamilies,
  displayGroupsWithFamilies,
  collapseProductGroups,
  hiddenGroups,
  includeOnlySelectedVariants,
  postFilters,
}: {
  preFilters?: Filter[]
  searchText?: string
  hasInsights?: boolean
  hasEdits?: boolean
  hasFavorites?: boolean
  hasPrintOptions?: boolean
  displayFamilies?: EnhancedProductFamilyId[]
  includeAllVariantsForMatchedFamilies?: boolean
  displayGroupsWithFamilies?: EnhancedProductGroupId[]
  collapseProductGroups?: Set<EnhancedProductGroupId>
  hiddenGroups?: EnhancedProductGroupId[]
  includeOnlySelectedVariants?: boolean
  postFilters?: Filter[]
}) =>
  (
    [
      ...(preFilters ?? []),
      includeOnlySelectedVariants && elementRemoveUnselectedVariantOperator,
      searchText && makeElementContainsStringOperator(searchText.toLowerCase(), { includeOnlySelectedVariants }),
      hasInsights && makeElementHasInsightsOperator({ includeOnlySelectedVariants }),
      hasEdits && elementHasBeenEditedOperator,
      hasFavorites && makeElementHasFavoritesOperator({ includeOnlySelectedVariants }),
      hasPrintOptions && elementIsPrinted,
      displayFamilies && makeElementKeepFamiliesOperator(displayFamilies),
      (includeAllVariantsForMatchedFamilies ?? searchText) && elementKeepUnfilteredVariantsAndFamiliesOperator,
      displayGroupsWithFamilies && makeElementKeepFamiliesOfGroupOperator(displayGroupsWithFamilies),
      collapseProductGroups && makeGroupIsCollapsedOperator(collapseProductGroups),
      hiddenGroups && makeGroupNotPartOfWellKnownOperator(hiddenGroups),
      ...(postFilters ?? []),
    ] as const
  ).filter((x) => !!x)

/** Filter product groups, its families and their variants based on the given operators, returns a lookup table if an element should be filtered or not */
export const filterConfiguration = (productGroups: AppProductGroup[], operators: Filter<unknown>[], comparator: FilterComparator) => {
  const bucket: FilterResult = {}
  const writer = makeBucketWriter(comparator, bucket)

  const filterGroup = (productGroup: AppProductGroup) => {
    for (const subGroup of productGroup.subGroups) {
      filterGroup(subGroup)
    }
    for (const filter of operators) {
      filter(productGroup, bucket, writer)
      for (const family of productGroup.families) {
        filter(family, bucket, writer)
        for (const variant of family.variants) {
          filter(variant, bucket, writer)
        }
      }
    }
  }

  for (const group of productGroups) {
    filterGroup(group)
  }
  return bucket
}

/** Filter product groups, its families and their variants based on the given operators, returns a lookup table if an element should be filtered or not */
export const filterProductGroups = (productGroups: AppProductGroup[], operators: Filter<unknown>[], comparator: FilterComparator = 'OR') => {
  return filterConfiguration(productGroups, operators, comparator)
}
